import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import {
  FileText, Link as LinkIcon, AlignLeft, Edit,
  Image as ImageIcon, AlertCircle, ArrowLeft, Save,
} from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import TipTapEditor from "./TipTapEditor";
import SocialLinksInput from "./SocialLinksInput";
import ArticleSnapshotsPanel from "./ArticleSnapshotsPanel";
import setAuthToken from "../../utils/setAuthToken";

import {
  PageHeader,
  Card,
  Input,
  Textarea,
  Select,
  Switch,
  FormField,
  Button,
  Spinner,
  SegmentedControl,
  DatePicker,
} from "../../design-system";

const SCHEDULE_PRESETS = [
  { label: 'Dans 1 heure',     value: () => { const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0); return d; } },
  { label: 'Demain 9h',        value: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
  { label: 'Lundi prochain 9h', value: () => {
    const d = new Date();
    const day = d.getDay();
    const add = day === 1 ? 7 : ((1 - day + 7) % 7) || 7;
    d.setDate(d.getDate() + add);
    d.setHours(9, 0, 0, 0);
    return d;
  } },
  { label: 'Vendredi 18h', value: () => {
    const d = new Date();
    const day = d.getDay();
    const target = 5;
    let add = (target - day + 7) % 7;
    if (add === 0 && d.getHours() >= 18) add = 7;
    d.setDate(d.getDate() + add);
    d.setHours(18, 0, 0, 0);
    return d;
  } },
  { label: 'Dans 1 semaine', value: () => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); return d; } },
];

const RANDOM_COLORS = [
  "#0f1936", "#1d2a4f", "#2c3a63", "#3d4d7a",
  "#6b7390", "#a89c7f", "#b8431a", "#3d6b3b",
  "#8a2f2a", "#2a4a7a", "#a06b1f",
];

class ArticleForm extends Component {
  constructor(props) {
    super(props);
    const { id } = this.props.match.params;
    this.state = {
      id: id || null,
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      type: "VEILLE_TECHNOLOGIQUE",
      status: "DRAFT",
      publishedAt: null,
      featured: false,
      imageUrl: "",
      color: id ? "" : this.generateRandomColor(),
      showToc: true,
      tocTitle: "Dans l'article",
      audioEnabled: true,
      metaTitle: "",
      metaDescription: "",
      loading: false,
      saving: false,
      imagePreview: null,
      imageError: false,
      socialLinks: [],
      authorId: "",
      admins: [],
    };
  }

  generateRandomColor = () => RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)];

  componentDidMount() {
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
    if (this.state.id) this.loadArticle();
    this.loadAdmins();
  }

  loadAdmins = async () => {
    try {
      const response = await axios.get("/api/users");
      const all = response.data.users || response.data || [];
      const admins = all.filter((u) => u.role === "ADMIN");
      this.setState({ admins });
    } catch (e) {
      console.warn("Impossible de charger la liste des admins :", e?.response?.data || e.message);
    }
  };

  loadArticle = async () => {
    try {
      this.setState({ loading: true });
      const response = await axios.get(`/api/articles/id/${this.state.id}`);
      const article = response.data.article;

      let parsedContent = "";
      if (article.content) {
        try {
          if (typeof article.content === "string") {
            const trimmed = article.content.trim();
            parsedContent = trimmed === "" || trimmed === "null" ? "" : JSON.parse(article.content);
          } else {
            parsedContent = article.content;
          }
        } catch (e) {
          console.warn("Erreur de parsing du contenu :", e);
          parsedContent = typeof article.content === "string" ? article.content : "";
        }
      }

      const imageUrl = article.imageUrl || "";

      this.setState({
        title: article.title || "",
        slug: article.slug || "",
        excerpt: article.excerpt || "",
        content: parsedContent,
        type: article.type || "VEILLE_TECHNOLOGIQUE",
        status: article.status || "DRAFT",
        publishedAt: article.publishedAt ? new Date(article.publishedAt) : null,
        featured: article.featured || false,
        imageUrl,
        color: article.color || this.generateRandomColor(),
        showToc: article.showToc !== false,
        tocTitle: article.tocTitle || "Dans l'article",
        audioEnabled: article.audioEnabled !== false,
        metaTitle: article.metaTitle || "",
        metaDescription: article.metaDescription || "",
        socialLinks: article.socialLinks ? JSON.parse(article.socialLinks) : [],
        authorId: article.authorId || article.author?.id || "",
        loading: false,
        imagePreview: imageUrl || null,
        imageError: false,
      });

      if (imageUrl) {
        setTimeout(() => {
          const img = document.createElement("img");
          img.onload  = () => this.setState({ imagePreview: imageUrl, imageError: false });
          img.onerror = () => this.setState({ imagePreview: null, imageError: true });
          img.src = imageUrl;
        }, 100);
      }
    } catch (error) {
      console.error("Error loading article:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || error.message || "Impossible de charger l'article",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  generateSlug = (title) =>
    title.toLowerCase()
      .normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  handleImageUrlChange = (e) => {
    const imageUrl = e.target.value;
    this.setState({ imageUrl, imageError: false, imagePreview: imageUrl || null });
    if (imageUrl) {
      const img = document.createElement("img");
      img.onload  = () => this.setState({ imagePreview: imageUrl, imageError: false });
      img.onerror = () => this.setState({ imagePreview: null, imageError: true });
      img.src = imageUrl;
    }
  };

  handleTitleChange = (e) => {
    const title = e.target.value;
    this.setState({ title });
    if (!this.state.slug || this.state.slug === this.generateSlug(this.state.title)) {
      this.setState({ slug: this.generateSlug(title) });
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ saving: true });
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);

      let contentToSend = this.state.content;
      if (typeof this.state.content === "string" && this.state.content.trim() !== "") {
        try { contentToSend = JSON.parse(this.state.content); }
        catch { /* envoie tel quel si pas JSON */ }
      }

      const articleData = {
        title: this.state.title,
        slug: this.state.slug,
        excerpt: this.state.excerpt,
        content: contentToSend,
        type: this.state.type,
        status: this.state.status,
        featured: this.state.featured,
        imageUrl: this.state.imageUrl,
        color: this.state.color,
        showToc: this.state.showToc,
        tocTitle: this.state.showToc ? this.state.tocTitle : null,
        audioEnabled: this.state.audioEnabled,
        metaTitle: this.state.metaTitle,
        metaDescription: this.state.metaDescription,
        socialLinks: this.state.socialLinks,
      };
      if (this.state.status === "SCHEDULED" && this.state.publishedAt) {
        articleData.publishedAt = this.state.publishedAt.toISOString();
      }
      if (this.state.id && this.state.authorId) articleData.authorId = this.state.authorId;

      if (this.state.id) await axios.put(`/api/articles/${this.state.id}`, articleData);
      else                await axios.post("/api/articles", articleData);

      Store.addNotification({
        title: `Article ${this.state.id ? "modifié" : "créé"}`,
        message: `« ${this.state.title} » enregistré.`,
        type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      this.props.history.push("/dashboard/articles");
    } catch (error) {
      console.error("Error saving article:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Erreur lors de la sauvegarde",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
      this.setState({ saving: false });
    }
  };

  render() {
    const { loading, saving } = this.state;
    const isEdit = !!this.state.id;
    const pageTitle = isEdit ? "Modifier l'article" : "Nouvel article";

    if (loading) {
      return (
        <AdminPageShell pageTitle={pageTitle}>
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    return (
      <AdminPageShell pageTitle={pageTitle} breadcrumbLast={isEdit ? (this.state.title || "Modifier") : undefined}>
        <PageHeader
          eyebrow="— Articles"
          title={pageTitle}
          description={isEdit ? "Mets à jour le contenu et les métadonnées." : "Crée un nouveau brouillon. Tu pourras le publier ensuite."}
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
                onClick={() => this.props.history.push("/dashboard/articles")}
              >
                Retour
              </Button>
              <Button
                type="submit"
                form="article-form"
                isLoading={saving}
                leftIcon={<Save className="w-4 h-4" strokeWidth={1.8} />}
              >
                {isEdit ? "Enregistrer" : "Créer"}
              </Button>
            </div>
          }
        />

        <form id="article-form" onSubmit={this.handleSubmit}>
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
            {/* Colonne principale : titre + contenu */}
            <div className="xl:col-span-3 space-y-5 min-w-0">
              <Card>
                <Card.Header><Card.Title>Identité</Card.Title></Card.Header>
                <div className="space-y-4">
                  <FormField label="Titre" required hint={<FileText className="w-3.5 h-3.5" />}>
                    <Input
                      value={this.state.title}
                      onChange={this.handleTitleChange}
                      required
                      autoFocus
                    />
                  </FormField>
                  <FormField
                    label="Slug"
                    required
                    helper="URL de l'article. Lettres minuscules, chiffres et tirets uniquement."
                    hint={<LinkIcon className="w-3.5 h-3.5" />}
                  >
                    <Input
                      value={this.state.slug}
                      onChange={(e) => this.setState({ slug: e.target.value })}
                      pattern="[a-z0-9-]+"
                      required
                    />
                  </FormField>
                  <FormField label="Extrait" hint={<AlignLeft className="w-3.5 h-3.5" />}>
                    <Textarea
                      rows={3}
                      value={this.state.excerpt}
                      onChange={(e) => this.setState({ excerpt: e.target.value })}
                      placeholder="Court résumé visible dans la liste et sur les cartes…"
                    />
                  </FormField>
                </div>
              </Card>

              <Card padding="none" className="overflow-hidden">
                <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                  <Edit className="w-4 h-4 text-text" strokeWidth={1.8} />
                  <h3 className="text-sm font-bold tracking-tight">Contenu</h3>
                </div>
                <div className="editor-container">
                  <TipTapEditor
                    content={this.state.content}
                    onChange={(content) => this.setState({ content })}
                    placeholder="Commencez à écrire votre article…"
                  />
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>SEO &amp; partage</Card.Title>
                  <Card.Description>Métadonnées pour les moteurs de recherche.</Card.Description>
                </Card.Header>
                <div className="space-y-4">
                  <FormField label="Titre SEO" helper="Idéalement 50–60 caractères.">
                    <Input
                      value={this.state.metaTitle}
                      onChange={(e) => this.setState({ metaTitle: e.target.value })}
                      placeholder={this.state.title || "Titre pour les moteurs de recherche"}
                    />
                  </FormField>
                  <FormField label="Description SEO" helper="Idéalement 120–155 caractères.">
                    <Textarea
                      rows={3}
                      value={this.state.metaDescription}
                      onChange={(e) => this.setState({ metaDescription: e.target.value })}
                      placeholder="Description pour les moteurs de recherche…"
                    />
                  </FormField>
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>Liens et sources</Card.Title>
                  <Card.Description>Sources, documentation, dépôt GitHub, démo ou ressources citées.</Card.Description>
                </Card.Header>
                <SocialLinksInput
                  value={this.state.socialLinks}
                  onChange={(socialLinks) => this.setState({ socialLinks })}
                />
              </Card>
            </div>

            {/* Sidebar : publication + métadonnées visuelles + snapshots */}
            <div className="space-y-5">
              <Card>
                <Card.Header><Card.Title>Publication</Card.Title></Card.Header>
                <div className="space-y-4">
                  <FormField label="Statut">
                    <div className="space-y-3">
                      <SegmentedControl
                        size="sm"
                        vertical
                        value={this.state.status === "ARCHIVED" ? "DRAFT" : this.state.status}
                        onChange={(v) => this.setState((s) => ({
                          status: v,
                          // Pré-remplit publishedAt avec demain 9h si on bascule en SCHEDULED sans date
                          publishedAt: v === "SCHEDULED" && !s.publishedAt
                            ? (() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; })()
                            : v === "DRAFT" ? null : s.publishedAt,
                        }))}
                        options={[
                          { value: "DRAFT",     label: "Brouillon" },
                          { value: "SCHEDULED", label: "Programmer" },
                          { value: "PUBLISHED", label: "Publier" },
                        ]}
                      />

                      {this.state.status === "SCHEDULED" && (
                        <DatePicker
                          value={this.state.publishedAt}
                          onChange={(d) => this.setState({ publishedAt: d })}
                          withTime
                          presets={SCHEDULE_PRESETS}
                          minDate={new Date()}
                          size="sm"
                        />
                      )}

                      {this.state.id && (
                        <button
                          type="button"
                          onClick={() => this.setState({
                            status: this.state.status === "ARCHIVED" ? "DRAFT" : "ARCHIVED",
                            publishedAt: this.state.status === "ARCHIVED" ? this.state.publishedAt : null,
                          })}
                          className="text-[11.5px] font-mono uppercase tracking-wider text-mute hover:text-text transition-colors inline-flex items-center gap-1"
                        >
                          {this.state.status === "ARCHIVED" ? "↩ Désarchiver" : "↘ Archiver l'article"}
                        </button>
                      )}

                      {this.state.status === "ARCHIVED" && (
                        <p className="text-[12px] text-mute italic">
                          Cet article est archivé — invisible côté public.
                        </p>
                      )}
                    </div>
                  </FormField>
                  <FormField label="Type">
                    <Select
                      value={this.state.type}
                      onChange={(e) => this.setState({ type: e.target.value })}
                      options={[
                        { value: "VEILLE_TECHNOLOGIQUE", label: "Veille technologique" },
                        { value: "VEILLE_JURIDIQUE",     label: "Veille juridique" },
                        { value: "STAGE",                label: "Stage" },
                        { value: "PPE",                  label: "PPE" },
                        { value: "PP",                   label: "PP" },
                        { value: "OTHER",                label: "Autre" },
                      ]}
                    />
                  </FormField>
                  <Switch
                    checked={this.state.featured}
                    onChange={(e) => this.setState({ featured: e.target.checked })}
                    label="Mettre en avant"
                    description="Apparaîtra sur la page d'accueil."
                  />
                  {this.state.id && this.state.admins.length > 0 && (
                    <FormField label="Auteur">
                      <Select
                        value={this.state.authorId}
                        onChange={(e) => this.setState({ authorId: e.target.value })}
                        options={this.state.admins.map((u) => ({
                          value: u.id,
                          label: `${u.name || u.email}${u.email && u.name ? ` (${u.email})` : ""}`,
                        }))}
                      />
                    </FormField>
                  )}
                </div>
              </Card>

              <Card>
                <Card.Header><Card.Title>Visuels</Card.Title></Card.Header>
                <div className="space-y-4">
                  <FormField label="Image de couverture (URL)" hint={<ImageIcon className="w-3.5 h-3.5" />}>
                    <Input
                      type="url"
                      placeholder="https://…"
                      value={this.state.imageUrl}
                      onChange={this.handleImageUrlChange}
                    />
                    {this.state.imageUrl && (
                      <div className="mt-2">
                        {this.state.imageError ? (
                          <div className="flex items-start gap-2 p-3 rounded-md bg-danger-bg text-danger text-xs">
                            <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                            Impossible de charger l'image
                          </div>
                        ) : this.state.imagePreview ? (
                          <img
                            src={this.state.imagePreview}
                            alt="Aperçu"
                            className="w-full max-h-40 object-cover rounded-md border border-border"
                            onError={() => this.setState({ imageError: true, imagePreview: null })}
                          />
                        ) : null}
                      </div>
                    )}
                  </FormField>
                  <FormField label="Couleur d'accent" helper="Utilisée pour les bordures et accents sur le site.">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={this.state.color || "#0f1936"}
                        onChange={(e) => this.setState({ color: e.target.value })}
                        className="w-10 h-10 rounded-md border border-border cursor-pointer p-0.5"
                      />
                      <Input
                        value={this.state.color || "#0f1936"}
                        onChange={(e) => {
                          const color = e.target.value;
                          if (/^#[0-9A-Fa-f]{0,6}$/.test(color)) this.setState({ color });
                        }}
                        placeholder="#0f1936"
                        className="font-mono"
                      />
                    </div>
                  </FormField>
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>Sommaire</Card.Title>
                  <Card.Description>Configure le bloc affiché à droite de l'article public.</Card.Description>
                </Card.Header>
                <div className="space-y-4">
                  <Switch
                    checked={this.state.showToc}
                    onChange={(e) => this.setState({ showToc: e.target.checked })}
                    label="Afficher le sommaire"
                    description="Le sommaire est généré automatiquement à partir des titres du contenu."
                  />
                  {this.state.showToc && (
                    <FormField label="Titre du sommaire">
                      <Input
                        value={this.state.tocTitle}
                        onChange={(e) => this.setState({ tocTitle: e.target.value })}
                        placeholder="Dans l'article"
                      />
                    </FormField>
                  )}
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>Écoute de l'article</Card.Title>
                  <Card.Description>Lecture audio (synthèse vocale) sur la page publique.</Card.Description>
                </Card.Header>
                <Switch
                  checked={this.state.audioEnabled}
                  onChange={(e) => this.setState({ audioEnabled: e.target.checked })}
                  label="Autoriser l'écoute"
                  description="Affiche un bouton « Écouter l'article » avec choix de la voix. Activé par défaut."
                />
              </Card>

              {this.state.id && (
                <ArticleSnapshotsPanel
                  articleId={this.state.id}
                  onRestored={() => this.loadArticle()}
                />
              )}

              <Card variant="filled">
                <div className="space-y-2">
                  <Button type="submit" fullWidth isLoading={saving} leftIcon={<Save className="w-4 h-4" />}>
                    {isEdit ? "Enregistrer" : "Créer l'article"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    fullWidth
                    onClick={() => this.props.history.push("/dashboard/articles")}
                  >
                    Annuler
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </AdminPageShell>
    );
  }
}

export default withRouter(ArticleForm);
